// ─────────────────────────────────────────────────────────────────────────────
// ScanPage.tsx
//
// Page 2 of the demo flow: a thin demo-owned shell around the SDK scanner UI.
//
// The SDK now owns camera setup, capture orientation, scan loop, overlay
// drawing, and progress/fit feedback. The demo keeps only its portal-style
// flow controls: back navigation.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { NDXReaderView } from '@novarumdx/ndx-imaging-web/react';
import type { NDXReader } from '@novarumdx/ndx-imaging-web/react';


export interface ScanPageProps {
  scanner: NDXReader;
  onCancel: () => void;
  previewMode?: 'fit' | 'fill';
  onPreviewModeChange?: (mode: 'fit' | 'fill') => void;
  readerAspect?: ReaderAspect;
  onReaderAspectChange?: (aspect: ReaderAspect) => void;
}

export type ReaderAspect = '4:3' | '16:9' | '1:1';


const ScanPage: React.FC<ScanPageProps> = ({
  scanner,
  onCancel,
  previewMode = 'fill',
  onPreviewModeChange = () => {},
  readerAspect = '4:3',
  onReaderAspectChange = () => {},
}) => {
  // True when all frames have been collected and the WASM engine is running
  // its analysis pass — typically 1–3 seconds for multi-strip cassettes.
  const isProcessing =
    scanner.maxProgress > 0 &&
    scanner.progress >= scanner.maxProgress &&
    scanner.status === 'scanning';

  const readerAreaRef = React.useRef<HTMLDivElement>(null);
  const readerBoxRef = React.useRef<HTMLDivElement>(null);
  const [isPortraitViewport, setIsPortraitViewport] = React.useState(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
  );

  React.useEffect(() => {
    const updateOrientation = () => setIsPortraitViewport(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', updateOrientation);
    return () => window.removeEventListener('resize', updateOrientation);
  }, []);

  React.useLayoutEffect(() => {
    const area = readerAreaRef.current;
    const box = readerBoxRef.current;
    if (!area || !box) return undefined;
    const sdkRoot = box.querySelector('.demo-reader-view');
    const cameraFrame = sdkRoot?.firstElementChild as HTMLElement | null;
    if (!sdkRoot || !cameraFrame) return undefined;
    const resizeReaderBox = () => {
      const video = box.querySelector('video');
      const isPortraitCamera = video && video.videoWidth && video.videoHeight
        ? video.videoHeight > video.videoWidth
        : isPortraitViewport;
      const [widthRatio, heightRatio] = readerAspect.split(':').map(Number);
      const ratio = isPortraitCamera ? heightRatio / widthRatio : widthRatio / heightRatio;
      const maxBoxWidth = isPortraitCamera ? 480 : 640;
      const maxBoxHeight = isPortraitCamera ? 640 : 480;
      const componentWidth = Math.min(Math.max(0, area.clientWidth - 32), 640);
      if (!componentWidth) return;
      // Keep the bundled status panel wide enough to remain readable. The
      // camera uses the available reader height; the status can extend below
      // it and the reader area scrolls when the landscape viewport is short.
      box.style.width = `${componentWidth}px`;
      box.style.height = 'auto';
      box.style.aspectRatio = 'auto';
      const maxHeight = Math.min(
        Math.max(0, area.clientHeight - 32),
        maxBoxHeight,
      );
      if (!maxHeight) return;
      const width = Math.min(componentWidth, maxBoxWidth, maxHeight * ratio);
      box.style.margin = '0 auto';
      box.style.setProperty('--demo-camera-width', `${width}px`);
      box.style.setProperty('--demo-camera-height', `${width / ratio}px`);
    };
    const video = box.querySelector('video');
    resizeReaderBox();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resizeReaderBox) : null;
    observer?.observe(area);
    observer?.observe(sdkRoot);
    video?.addEventListener('loadedmetadata', resizeReaderBox);
    video?.addEventListener('resize', resizeReaderBox);
    window.addEventListener('resize', resizeReaderBox);
    return () => {
      observer?.disconnect();
      video?.removeEventListener('loadedmetadata', resizeReaderBox);
      video?.removeEventListener('resize', resizeReaderBox);
      window.removeEventListener('resize', resizeReaderBox);
    };
  }, [readerAspect, isPortraitViewport, previewMode]);

  const readerAspectLabel = (aspect: ReaderAspect) => (
    isPortraitViewport ? aspect.split(':').reverse().join(':') : aspect
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'black',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          gap: 1,
          px: 1,
          pt: 'calc(8px + env(safe-area-inset-top))',
          pb: 1,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
        }}
      >
        <IconButton onClick={onCancel} sx={{ color: 'white' }} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 108 }}>
          <InputLabel
            id="reader-box-label"
            sx={{ color: 'rgba(255,255,255,0.75)', '&.Mui-focused': { color: 'white' } }}
          >
            Reader box
          </InputLabel>
          <Select
            labelId="reader-box-label"
            value={readerAspect}
            label="Reader box"
            onChange={(event) => onReaderAspectChange(event.target.value as ReaderAspect)}
            inputProps={{ 'aria-label': 'Reader box aspect ratio' }}
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '.MuiSvgIcon-root': { color: 'white' },
            }}
          >
            <MenuItem value="4:3">{readerAspectLabel('4:3')}</MenuItem>
            <MenuItem value="16:9">{readerAspectLabel('16:9')}</MenuItem>
            <MenuItem value="1:1">{readerAspectLabel('1:1')}</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={previewMode}
          exclusive
          size="small"
          aria-label="Camera preview mode"
          onChange={(_event, next: 'fit' | 'fill' | null) => {
            if (next !== null) onPreviewModeChange(next);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              color: 'white',
              borderColor: 'rgba(255,255,255,0.4)',
              px: 1.5,
              py: 0.25,
              lineHeight: 1.4,
            },
            '& .Mui-selected': {
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.24)',
            },
          }}
        >
          <ToggleButton value="fill" aria-label="Fill">Fill</ToggleButton>
          <ToggleButton value="fit" aria-label="Fit">Fit</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        ref={readerAreaRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          '@supports (align-items: safe center)': {
            alignItems: 'safe center',
          },
          justifyContent: 'center',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: 'black',
        }}
      >
        <Box
          ref={readerBoxRef}
          data-testid="reader-box"
          data-reader-aspect={readerAspect}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            flex: '0 1 auto',
            '& .demo-reader-view': {
              height: 'auto !important',
            },
            '& .demo-reader-view > div:first-child': {
              width: 'var(--demo-camera-width) !important',
              height: 'var(--demo-camera-height) !important',
              flex: '0 0 auto !important',
              visibility: scanner.cameraReady ? 'visible' : 'hidden',
            },
          }}
        >
          <NDXReaderView
            scanner={scanner}
            className="demo-reader-view"
            style={{ width: '100%', height: 'auto' }}
          />

          {/* Processing overlay — shown while WASM analysis runs after last frame */}
          {isProcessing && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: 'rgba(0,0,0,0.72)',
              }}
            >
              <CircularProgress size={48} sx={{ color: '#64dd17' }} />
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                Processing…
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ScanPage;
